import { useCallback, useEffect, useMemo, useState } from "react";
import { useDataChanged } from "../useDataChanged";
import { AppShell } from "../components/AppShell";
import { OverviewTopbar, ProjectsRow } from "../components/OverviewTopbar";
import { ScanStrip } from "../components/ScanStrip";
import { IconChevronDown } from "../components/icons";
import { CardSkeleton } from "../components/Skeletons";
import { useI18n, type TranslationKey } from "../i18n";
import {
  api,
  isProjectVerified,
  normalizeApiText,
  type Finding,
  type ReportVerificationInfo,
} from "../api";
import { useAppState } from "../AppStateContext";
import { strictModeNotice, truncatedNotice } from "../reportVerification";
import "./overview.css";
import "./findings.css";

type SeverityTab = "All" | "High" | "Medium" | "Low";

const SEVERITY_TABS: { id: SeverityTab; labelKey: TranslationKey }[] = [
  { id: "All", labelKey: "findings.tabAll" },
  { id: "High", labelKey: "findings.tabHigh" },
  { id: "Medium", labelKey: "findings.tabMedium" },
  { id: "Low", labelKey: "findings.tabLow" },
];

function matchesTab(tab: SeverityTab, finding: Finding): boolean {
  if (tab === "All") return true;
  const sev = normalizeApiText(finding.severity);
  if (tab === "High") return sev === "critical" || sev === "high";
  if (tab === "Medium") return sev === "medium";
  return sev === "low" || sev === "info";
}

export default function FindingsPage() {
  const { projects, activeProject, setActiveProjectId, activeScan, viewedScan } = useAppState();
  const { t } = useI18n();
  const displayScan = viewedScan ?? activeScan;
  const [findings, setFindings] = useState<Finding[]>([]);
  // Upstream total_count: the gateway only ever returns the first 200, so
  // this can exceed findings.length (planning#213).
  const [total, setTotal] = useState<number | null>(null);
  // Manual attestation state; once strict_mode is on, the owner list is
  // limited to pentester-confirmed findings and the page says so.
  const [verification, setVerification] = useState<ReportVerificationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<SeverityTab>("All");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [dataTick, setDataTick] = useState(0);
  useDataChanged(useCallback(() => setDataTick((n) => n + 1), []));
  const displayScanId = displayScan?.scan_id ?? null;

  useEffect(() => {
    let cancelled = false;
    if (!displayScanId) {
      const resetId = window.setTimeout(() => {
        if (!cancelled) {
          setFindings([]);
          setTotal(null);
          setVerification(null);
          setExpanded(null);
          setError(null);
          setLoading(false);
        }
      }, 0);
      return () => {
        cancelled = true;
        window.clearTimeout(resetId);
      };
    }
    const tick = async () => {
      try {
        // Verification is best-effort: a failed lookup must not hide the list.
        const [r, v] = await Promise.all([
          api.scanFindings(displayScanId),
          api.getReportVerification(displayScanId).catch(() => null),
        ]);
        if (!cancelled) {
          setFindings(r.findings);
          setTotal(r.total);
          setVerification(v);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : t("findings.loading"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    setError(null);
    const initialId = window.setTimeout(() => void tick(), 0);
    const id = window.setInterval(tick, 10_000);
    return () => {
      cancelled = true;
      window.clearTimeout(initialId);
      window.clearInterval(id);
    };
  }, [displayScanId, dataTick]);

  const filtered = useMemo(
    () => findings.filter((f) => matchesTab(activeTab, f)),
    [findings, activeTab],
  );
  const strict = strictModeNotice(verification);
  const truncated = truncatedNotice(findings.length, total);

  return (
    <AppShell>
      <div className="overview anim-page">
        <OverviewTopbar />
        <ProjectsRow
          searches={projects.map((p) => ({
            id: p.project_id,
            label: p.name,
            verified: isProjectVerified(p),
          }))}
          activeId={activeProject?.project_id}
          onSelect={setActiveProjectId}
        />
        <ScanStrip />

        <div className="findings__header">
          <p className="overview__section-label" style={{ margin: 0 }}>
            {t("findings.sectionLabel", { count: findings.length })}
          </p>
          <div className="findings__tabs" data-tour="severity-tabs">
            {SEVERITY_TABS.map(({ id, labelKey }) => (
              <button
                key={id}
                type="button"
                className={`findings__tab${activeTab === id ? " findings__tab--active" : ""}`}
                onClick={() => setActiveTab(id)}
              >
                {t(labelKey)}
              </button>
            ))}
          </div>
        </div>

        {error ? <p className="auth-error">{error}</p> : null}
        {strict ? (
          <p className="findings__notice findings__notice--strict">
            {t("findings.strictNotice", strict)}
          </p>
        ) : null}
        {truncated ? (
          <p className="findings__notice">{t("findings.truncatedNotice", truncated)}</p>
        ) : null}

        {loading && filtered.length === 0 ? (
          <div className="findings__list">
            <CardSkeleton count={4} />
          </div>
        ) : filtered.length === 0 ? (
          <p className="helper">
            {displayScan ? t("findings.emptyFiltered") : t("findings.emptyNoScan")}
          </p>
        ) : (
          <div
            className="findings__list anim-stagger"
            key={`findings-${activeTab}`}
            style={{ ["--anim-stagger-step" as never]: "50ms" }}
          >
            {filtered.map((f, i) => (
              <FindingCard
                key={f.finding_id}
                finding={f}
                scanId={displayScan?.scan_id ?? ""}
                expanded={expanded === f.finding_id}
                anchor={i === 0 ? "finding-card" : undefined}
                onToggle={() =>
                  setExpanded((cur) => (cur === f.finding_id ? null : f.finding_id))
                }
              />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function FindingCard({
  finding,
  expanded,
  onToggle,
  anchor,
}: {
  finding: Finding;
  scanId: string;
  expanded: boolean;
  onToggle: () => void;
  anchor?: string;
}) {
  const { t } = useI18n();
  const status = displayStatus(finding, t);
  const severity = displaySeverity(finding.severity, t);
  const titleText = finding.title || finding.kind || t("findings.untitled");
  const target = finding.target_url || finding.target || finding.target_host || finding.url;

  return (
    <article className={`finding-card${expanded ? " finding-card--expanded" : ""}`} data-tour={anchor}>
      <button type="button" className="finding-card__head" onClick={onToggle}>
        <div className="finding-card__title-block">
          <p className="finding-card__title">{titleText}</p>
          <div className="finding-card__badges">
            <span className={`finding-badge finding-badge--status finding-badge--${status.tone}`}>
              {status.label}
            </span>
            <span className={`finding-badge finding-badge--severity finding-badge--${severity.tone}`}>
              {severity.label}
            </span>
          </div>
        </div>
        <span
          className="finding-card__chevron"
          style={{ transform: expanded ? "rotate(180deg)" : undefined }}
          aria-hidden
        >
          <IconChevronDown width={20} height={20} />
        </span>
      </button>

      {expanded ? (
        <div className="finding-card__body">
          <FieldRow label={t("findings.target")} value={target} />
          <FieldRow label={t("findings.vulnType")} value={finding.vuln_type} />
          <FieldRow label={t("findings.tool")} value={finding.tool_name} />
          <FieldRow
            label={t("findings.description")}
            value={finding.description ?? finding.evidence}
            multiline
          />
          {finding.remediation ? (
            <FieldRow
              label={t("findings.recommendation")}
              value={finding.remediation}
              multiline
              anchor="finding-fix"
            />
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function FieldRow({
  label,
  value,
  multiline = false,
  mono = false,
  anchor,
}: {
  label: string;
  value?: string | number | null;
  multiline?: boolean;
  mono?: boolean;
  anchor?: string;
}) {
  const { t } = useI18n();
  const text =
    value === null || value === undefined || value === ""
      ? t("findings.notAvailable")
      : String(value);
  return (
    <div className="finding-field" data-tour={anchor}>
      <p className="finding-field__label">{label}</p>
      <div
        className={`finding-field__value${multiline ? " finding-field__value--multiline" : ""}${
          mono ? " finding-field__value--mono" : ""
        }`}
      >
        {multiline ? <pre>{text}</pre> : <span>{text}</span>}
      </div>
    </div>
  );
}

type TFn = (key: TranslationKey, vars?: Record<string, string | number>) => string;

function displayStatus(
  finding: Finding,
  t: TFn,
): { label: string; tone: "confirmed" | "open" | "rejected" } {
  if (finding.is_false_positive) {
    return { label: t("findings.statusRejected"), tone: "rejected" };
  }
  if (finding.is_verified) {
    return { label: t("findings.statusConfirmed"), tone: "confirmed" };
  }
  const s = normalizeApiText(finding.status ?? "");
  if (s === "confirmed" || s === "ready") return { label: t("findings.statusConfirmed"), tone: "confirmed" };
  if (s === "rejected" || s === "fp" || s === "false_positive")
    return { label: t("findings.statusRejected"), tone: "rejected" };
  // Backend legacy values like "pending"/"open"/"new" all map to the same
  // "open" tone; never leak the raw English string into the RU UI.
  return { label: t("findings.statusOpen"), tone: "open" };
}

function displaySeverity(
  severity: string,
  t: TFn,
): { label: string; tone: "critical" | "high" | "medium" | "low" } {
  const s = normalizeApiText(severity);
  if (s === "critical") return { label: t("findings.sevCritical"), tone: "critical" };
  if (s === "high") return { label: t("findings.sevHigh"), tone: "high" };
  if (s === "medium") return { label: t("findings.sevMedium"), tone: "medium" };
  if (s === "low") return { label: t("findings.sevLow"), tone: "low" };
  // Unknown/empty severity: show the raw value if present, else default to low.
  return { label: severity || t("findings.sevLow"), tone: "low" };
}

